import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '~/components/Button'
import { RadioButton } from '~/components/RadioButton'
import { SelectInput } from '~/components/SelectInput'
import { MaskedInput, TextInput } from '~/components/TextInput'
import { PaymentPlan } from '~/modules/sales/types'
import { requestAddressViaCEP } from '~/modules/viacep/services'
import { debouncer } from '~/utils/debounce'

import { cardDataFormSchema, CardDataFormValues } from './schema'

type Props = {
  paymentPlan: PaymentPlan
  onSubmit(payload: CardDataFormValues): Promise<void>
}

const debounce = debouncer()

export const CardForm: React.FC<Props> = ({ onSubmit, paymentPlan }) => {
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false)
  const availableMethods = paymentPlan.metaData.paymentMethods.filter((method) =>
    ['credit_card', 'debit_card'].includes(method),
  )

  const { register, control, formState, handleSubmit, setValue, watch } =
    useForm<CardDataFormValues>({
      resolver: zodResolver(cardDataFormSchema),
      defaultValues: {
        installments: 1,
        billing_address: {
          country: 'BR',
        },
        payment_method:
          availableMethods.length === 1
            ? (availableMethods[0] as any)
            : undefined,
      },
    })

  const installmentsOptions = useMemo(() => {
    return Array.from({ length: paymentPlan.installments || 12 }, (_, i) => ({
      label: String(i + 1),
      value: i + 1,
    }))
  }, [paymentPlan.installments])

  const submitForm = useCallback(
    async (payload: CardDataFormValues) => {
      await onSubmit(payload)
    },
    [onSubmit],
  )

  return (
    <form className='grid gap-4' onSubmit={handleSubmit(submitForm)}>
      <div className='flex flex-col gap-4'>
        <p>Tipo de Pagamento</p>
        <div className='flex self-start gap-5'>
          {paymentPlan.metaData.paymentMethods.includes('credit_card') ? (
            <RadioButton
              label='Crédito'
              value='credit_card'
              hasError={!!formState.errors.payment_method?.message}
              {...register('payment_method')}
            />
          ) : null}
          {paymentPlan.metaData.paymentMethods.includes('debit_card') ? (
            <RadioButton
              label='Débito'
              value='debit_card'
              hasError={!!formState.errors.payment_method?.message}
              {...register('payment_method')}
            />
          ) : null}
        </div>
        <span className='text-xs text-gpa-red'>
          {formState.errors.payment_method?.message}
        </span>
      </div>
      <Controller
        control={control}
        name='number'
        render={({ field }) => (
          <MaskedInput
            label='Número do cartão'
            error={formState.errors.number?.message}
            placeholder='0000 0000 0000 0000'
            mask='creditCard'
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <TextInput
        {...register('holder_name')}
        label='Nome como impresso no cartão'
        error={formState.errors.holder_name?.message}
      />

      <div>
        <div className='grid gap-4 md:grid-cols-2'>
          <Controller
            control={control}
            name='expiration'
            render={({ field }) => (
              <MaskedInput
                label='Data de validade'
                error={formState.errors.expiration?.message}
                placeholder='00/00'
                mask='monthYear'
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name='cvv'
            render={({ field }) => (
              <MaskedInput
                label='Código de segurança'
                error={formState.errors.cvv?.message}
                placeholder='000'
                mask='cvv'
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        {watch('payment_method') === 'credit_card' &&
          (paymentPlan.installments || 12) > 1 ? (
          <>
            <p>Número de Parcelas</p>
            <Controller
              control={control}
              name='installments'
              render={({ field }) => (
                <SelectInput
                  value={field.value}
                  options={installmentsOptions}
                  onChange={field.onChange}
                />
              )}
            />
            {formState.errors.installments?.message ? (
              <span className='text-xs text-gpa-red'>
                {formState.errors.installments?.message}
              </span>
            ) : null}
          </>
        ) : null}

        <div className='mt-6'>
          <h3 className='text-lg font-semibold mb-4'>Endereço de Cobrança</h3>

          <div className='flex flex-col gap-2'>
            <div className='flex flex-col bg-slate-50 p-4 rounded-lg border'>
              <Controller
                control={control}
                name='billing_address.zip_code'
                render={({ field }) => (
                  <MaskedInput
                    mask='zipCode'
                    label='CEP'
                    loading={loadingAddress}
                    placeholder='00000-000'
                    value={field.value}
                    error={formState.errors.billing_address?.zip_code?.message}
                    onChange={(v: string) => {
                      if (v.length === 9) {
                        debounce(() => {
                          setLoadingAddress(true)
                          requestAddressViaCEP(v.replace(/\D/g, ''))
                            .then((data) => {
                              if (data) {
                                setValue(
                                  'billing_address.city',
                                  data.localidade,
                                )
                                setValue('billing_address.state', data.uf)
                                setValue(
                                  'billing_address.line_1',
                                  data.logradouro,
                                )
                                setValue(
                                  'billing_address.line_2',
                                  data.complemento,
                                )
                              }
                            })
                            .finally(() => {
                              setLoadingAddress(false)
                            })
                        })
                      }
                      field.onChange(v)
                    }}
                  />
                )}
              />
              <a
                href='http://www.buscacep.correios.com.br/sistemas/buscacep/'
                rel='noopener noreferrer'
                target='_blank'
                className='underline mb-2 text-sm text-gpa-blue-500 hover:text-blue-800'
              >
                Não sei meu CEP
              </a>

              <TextInput
                label='Logradouro'
                {...register('billing_address.line_1')}
                error={formState.errors.billing_address?.line_1?.message}
              />
              <TextInput
                label='Complemento'
                {...register('billing_address.line_2')}
                error={formState.errors.billing_address?.line_2?.message}
              />
              <div className='flex gap-3'>
                <TextInput
                  label='Cidade'
                  placeholder='Niterói'
                  {...register('billing_address.city')}
                  error={formState.errors.billing_address?.city?.message}
                />
                <Controller
                  control={control}
                  name='billing_address.state'
                  render={({ field }) => (
                    <TextInput
                      label='Estado'
                      error={formState.errors.billing_address?.state?.message}
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        {/* <div className='my-6'>área para termos e condições</div> */}

        <Button
          disabled={formState.isSubmitting}
          loading={formState.isSubmitting}
          loadingMessage='Finalizando pagamento...'
          className='w-full mt-4'
        >
          Finalizar Pedido
        </Button>
      </div>
    </form>
  )
}
